import { sha256 } from 'js-sha256';

/**
 * Sanitizes a graph restId that comes in as a query. Series instances
 * contain a forward slash that must be replaced with a dash before
 * graph can be queried.
 * @param {string} eventId A user's event Id
 * @return {string} sanitized graph restId
 */
export const sanitizeEventId = (eventId) => eventId.replace(/\//g, '-');

/**
 * Generic function for calculating the hash of an object
 * @param {Object} objectToHash Some object to be hashed
 * @return {string} hash value
 */
export const calculateObjectHash = (objectToHash) => sha256(JSON.stringify(objectToHash));

const capitalize = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const ordinal = (str) => {
  const dateNum = parseInt(str);
  if (dateNum > 3 && dateNum < 21) return str + 'th';
  switch (dateNum % 10) {
    case 1:
      return str + 'st';
    case 2:
      return str + 'nd';
    case 3:
      return str + 'rd';
    default:
      return str + 'th';
  }
};

const dayMap = {
  sun: 'Sunday',
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
};

export const recurrenceMap = (recurrenceObj) => {
  const properties = recurrenceObj.recurrenceProperties;

  switch (recurrenceObj.recurrenceType) {
    case 'daily':
      return 'Daily';
    case 'weekday':
      return `Daily`;
    case 'weekly':
      return properties.interval === 1 ?
       `Weekly on ${properties.days.map((day) => ' ' + dayMap[day])}` :
       `Every ${properties.interval} weeks on ${properties.days.map((day) => ' ' + dayMap[day])}`;
    case 'monthly':
      return properties.dayOfMonth ?
        `Monthly on the ${ordinal(properties.dayOfMonth)}` :
        `Monthly on every ${properties.weekNumber} ${dayMap[properties.dayOfWeek]}`;
    case 'yearly':
      return properties.dayOfMonth ?
        `Yearly on ${capitalize(properties.month)}. ${properties.dayOfMonth}` :
        `Yearly on the ${properties.weekNumber} ${dayMap[properties.dayOfWeek]} of ${capitalize(properties.month)}.`;
    default:
      return 'on a custom interval';
  }
};

export const mockOrganizers = [
  {
    id: '12123-12312-6756741',
    name: 'Logan Organizer',
    details: ['lkarnes@test.com', '4335 NE 66th Ave Vancouver Wa 98661', '(360) 600-1869'],
    redirect: 'https://www.google.com',
  },
  {
    id: '12123-12312-6756742',
    name: 'Bill Nye',
    details: ['bnye@test.com', '2415 Eisenhower Ave Alexandria VA 22314', '(703) 555-1234'],
    redirect: 'https://www.google.com',
  },
  {
    id: '12123-12312-6756743',
    name: 'The Science Guy',
    details: ['bnye@test.com', '2415 Eisenhower Ave Alexandria VA 22314', '(703) 555-1234'],
    redirect: 'https://www.google.com',
  },
];

export const mockAttendees = [
  {
    id: '12123-12312-6756745',
    name: 'Michael Jackson',
    details: ['mj@litify.com', '3234 NE 56th Ave Spokane Wa 99224', '(360) 600-1869'],
    redirect: 'https://www.google.com',
  },
  {
    id: '12123-12312-3423425',
    name: 'Jane Doe',
    details: ['jdoe@litify.com', '4552 NW 34th Ave Vancouver Wa 98661', '(360) 600-1869'],
    redirect: 'https://www.google.com',
  },
  {
    id: '12123-12312-34523423',
    name: 'Logan Attendee',
    details: ['lattendee@litify.com', '4552 NW 34th Ave Vancouver Wa 98661', '(360) 600-1869'],
    redirect: 'https://www.google.com',
  },
  {
    id: '12123-12312-3458565',
    name: 'Logan Dev',
    details: ['ldev@litify.com', '4552 NW 34th Ave Vancouver Wa 98661', '(360) 600-1869'],
    redirect: 'https://www.google.com',
  },
  {
    id: '1',
    email: 'test@litify.com',
    name: 'Peregrine Took',
    details: ['test@litify.com', '3234 NE 56th Ave Spokane Wa 99224', '(360) 600-1869'],
    redirect: 'https://www.google.com',
  },
  {
    id: '2',
    email: 'test@litify.com',
    name: 'Pippin Took',
    details: ['test@litify.com', '3234 NE 56th Ave Spokane Wa 99224', '(360) 600-1869'],
    redirect: 'https://www.google.com',
  },
  {
    id: '3',
    email: 'test@litify.com',
    name: 'Pip',
    details: ['test@litify.com', '3234 NE 56th Ave Spokane Wa 99224', '(360) 600-1869'],
    redirect: 'https://www.google.com',
  },
  {
    id: '12321-66345-93344553',
    email: 'dattendee@litify.com',
    name: 'Drew Dev',
    details: ['dattendee@litify.com', '123 Ocean Ave Los Angelese CA 90210', '(555) 555-1234'],
    redirect: 'https://www.google.com',
  },
  {
    id: '12321-66345-93344556',
    email: 'dattendee@litify.com',
    name: 'Drew Dev #2',
    details: ['dattendee@litify.com', '18 W 53rd ST New York NY 10019', '(555) 555-1234'],
    redirect: 'https://www.google.com',
  },
];

export const handleCreateContactDesktop = async (url) => {
  // We cannot use the dialog option for calendar, as it requires the domain to be added to the manifest.
  // Because each of our customers have a unique domain, there's no way for us to accomplish this. Instead,
  // we will simply open a browser window to the entry form and allow them to create it that way. They will
  // need to use the refresh button to get the updated list. Auto-refresh will only work on Web.
  try {
    window.open(
        url,
        '_blank',
        `popup,
        width=550, 
        height=750, 
        left=${(window.screen.width - 550) / 2}, 
        top=${(window.screen.height - 650) / 2}`,
    );
  } catch (e) {
    console.error(e);
    throw new Error(`An error occurred while creating new contact: ${e.message}`);
  }
};

export const handleCreateContactBrowser = async (url) => {
  try {
    const contactWindow = window.open(
        url,
        'Docrio Email Calendar',
        `width=550, 
        height=750, 
        left=${(window.screen.width - 550) / 2}, 
        top=${(window.screen.height - 650) / 2}`,
    );

    const isWindowClosed = () => {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          if (contactWindow.closed) {
            resolve(clearInterval(interval));
          }
        }, 200);
      });
    };

    await isWindowClosed();
  } catch (e) {
    console.error(e);
    throw new Error(`An error occurred while creating new contact: ${e.message}`);
  }
};
